import { ConfirmDialog, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { CompanyValue } from '@/types/company-values';

import { useGetCompanyValueImpactCounts, useDeleteCompanyValue } from '@/api/company-values';
import { getLocalizedText } from '@/utils/localization';

interface DeleteValueDialogProps {
    isOpen: boolean;
    value: CompanyValue | null;
    onCancel: () => void;
    onSuccess: () => void;
}

export default function DeleteValueDialog({
    isOpen,
    value,
    onCancel,
    onSuccess,
}: DeleteValueDialogProps) {
    const { t } = useTranslation();
    const impactQuery = useGetCompanyValueImpactCounts(value?.id ?? '', isOpen);
    const deleteMutation = useDeleteCompanyValue();

    const handleDelete = async () => {
        if (!value) return;

        try {
            await deleteMutation.mutateAsync(value.id);
            toast.success(t('visionValues.deleteSuccess', { name: getLocalizedText(value.name) }));
            onSuccess();
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : t('visionValues.deleteError');
            toast.error(errorMessage);
        }
    };

    const impactCounts = impactQuery.data;
    const canDelete = impactCounts?.inProgressEvaluationCount === 0;

    return (
        <ConfirmDialog
            isOpen={isOpen}
            title={t('visionValues.deleteValueTitle', { name: getLocalizedText(value?.name) })}
            message={
                <div className={'flex flex-col gap-4'}>
                    <Text>{t('visionValues.deleteValueMessage')}</Text>

                    {impactCounts && (
                        <div className={'rounded-lg bg-gray-50 p-4'}>
                            <Text className={'mb-2 font-medium'}>
                                {t('visionValues.impactInformation')}:
                            </Text>

                            <ul className={'list-disc space-y-1 pl-5 text-[14px] text-gray-600'}>
                                <li>
                                    {t('visionValues.inProgressEvaluations')}:{' '}
                                    {impactCounts.inProgressEvaluationCount}
                                </li>

                                <li>
                                    {t('visionValues.employeesWithHistoricalData')}:{' '}
                                    {impactCounts.evaluatedEmployeeCount}
                                </li>

                                <li>
                                    {t('visionValues.associatedQuestions')}:{' '}
                                    {impactCounts.questionCount}
                                </li>
                            </ul>
                        </div>
                    )}

                    {!canDelete && (
                        <Text className={'text-orange-600'}>
                            {t('visionValues.deleteBlockedMessage')}
                        </Text>
                    )}
                </div>
            }
            confirmLabel={t('visionValues.delete')}
            cancelLabel={t('common.cancel')}
            onConfirm={handleDelete}
            onCancel={onCancel}
            isLoading={deleteMutation.isPending}
            isConfirmDisabled={!canDelete}
            isDestructive
            dataTestId={'delete-value-dialog'}
        />
    );
}
